import { type HttpClient, HttpEventType, type HttpRequest, type HttpResponse } from '@angular/common/http';

import { filter } from 'rxjs';

/**
 * Sends a generated request builder's `HttpRequest` through `HttpClient` so that aborting the signal unsubscribes it,
 * which cancels the HTTP request. Only an `HttpClient` subscription can be cancelled this way; the SDK promise
 * functions cannot.
 *
 * @returns The response body. Rejects with the `HttpErrorResponse`, with an error for an empty body, or with the
 *   signal's reason when aborted.
 */
export function sendCancellable<TResponse>(
  httpClient: HttpClient,
  request: HttpRequest<unknown>,
  signal?: AbortSignal,
): Promise<TResponse> {
  if (signal?.aborted) {
    return Promise.reject(signal.reason);
  }

  return new Promise<TResponse>((resolve, reject) => {
    const subscription = httpClient
      .request<TResponse>(request)
      .pipe(
        filter((event): event is HttpResponse<TResponse> => event.type === HttpEventType.Response),
      )
      .subscribe({
        next: (response) =>
          response.body === null
            ? reject(new Error(`Empty response body from ${request.urlWithParams}`))
            : resolve(response.body),
        error: reject,
      });

    if (!signal) {
      return;
    }

    const handleAbort = () => {
      subscription.unsubscribe();
      reject(signal.reason);
    };
    signal.addEventListener('abort', handleAbort, { once: true });
    subscription.add(() => signal.removeEventListener('abort', handleAbort));
  });
}
